import { useQuery } from '@tanstack/react-query';
import { BatteryCharging, Lock, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CartFormModal } from '../components/cart-dialogs';
import { Badge, Button, Card, EmptyState, ErrorState, Input, PageHeader, Select, Spinner, Table, Td, Th } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { useBuildings, useDebouncedValue, useUrlState } from '../lib/hooks';
import type { CartSummary } from '../lib/types';

const DEFAULTS = { search: '', buildingId: '', status: 'ACTIVE' };

export function CartsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const buildings = useBuildings();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = useQuery({
    queryKey: ['carts', filters],
    queryFn: () =>
      api.get<CartSummary[]>('/carts', {
        search: filters.search,
        buildingId: filters.buildingId,
        ...(filters.status === 'ALL' ? {} : { status: filters.status }),
      }),
  });

  return (
    <>
      <PageHeader
        title="Carts"
        description="Carts that hold and charge laptops and iPads. A cart’s home building and room are the location of every device on it."
        actions={
          can('TECHNICIAN') && (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              New cart
            </Button>
          )
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search carts, labels, or rooms" className="w-full sm:w-72" />
          <Select value={filters.buildingId} onChange={(e) => setFilters({ buildingId: e.target.value })} className="w-auto">
            <option value="">All buildings</option>
            {buildings.data?.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </Select>
          <Select value={filters.status} onChange={(e) => setFilters({ status: e.target.value })} className="w-auto">
            <option value="ACTIVE">In use</option>
            <option value="RETIRED">Retired</option>
            <option value="ALL">All carts</option>
          </Select>
          {query.isFetching && !query.isLoading && <Spinner className="size-4" />}
        </div>

        {query.isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner />
          </div>
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data!.length === 0 ? (
          <EmptyState
            icon={<BatteryCharging />}
            title={filters.search || filters.buildingId ? 'No carts match' : 'No carts yet'}
            description="Add a cart, then put devices on it. Moving the cart moves them all."
            action={can('TECHNICIAN') && !filters.search && <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>New cart</Button>}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Cart</Th>
                <Th>Home</Th>
                <Th>Devices</Th>
                <Th>Lease</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {query.data!.map((cart) => (
                <tr key={cart.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/carts/${cart.id}`)}>
                  <Td>
                    <Link to={`/carts/${cart.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-700">
                      {cart.name}
                    </Link>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                      {cart.code && <span className="font-mono">{cart.code}</span>}
                      {cart.status === 'RETIRED' && <Badge>Retired</Badge>}
                    </div>
                  </Td>
                  <Td>
                    <p className="text-slate-800">{cart.homeRoom?.name ?? <span className="text-slate-400">No room</span>}</p>
                    <p className="text-xs text-slate-500">
                      {cart.homeBuilding.name}
                      {cart.homeBuilding.kind === 'WAREHOUSE' && ' · off campus'}
                    </p>
                  </Td>
                  <Td className="whitespace-nowrap">
                    {cart.deviceCount}
                    {cart.capacity ? <span className="text-slate-400"> / {cart.capacity}</span> : null}
                  </Td>
                  <Td>
                    {cart.activeLease ? (
                      <>
                        <Badge tone="amber">
                          <Lock className="size-3" /> {cart.activeLease.assigneeLabel}
                        </Badge>
                        {cart.activeLease.dueAt && <p className="mt-0.5 text-xs text-slate-500">Due {formatDate(cart.activeLease.dueAt)}</p>}
                      </>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <CartFormModal open={creating} onClose={() => setCreating(false)} onSaved={(cart) => navigate(`/carts/${cart.id}`)} />
    </>
  );
}
